import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Briefcase, Calendar, Download, Building2, FileText } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { getVacancies } from "../lib/api";

export default function Vacancies() {
  const [list, setList] = useState([]);

  useEffect(() => {
    getVacancies().then(setList).catch(() => setList([]));
  }, []);

  return (
    <div data-testid="vacancies-page">
      <PageHeader
        eyebrow="Careers at our college"
        title="Current vacancies."
        subtitle="Join the Jawad Ali Shah Imambara legacy. Latest openings — both teaching and non-teaching posts as advertised in newspapers."
      />

      <section className="py-16 lg:py-24 bg-brand-ivory">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {list.length === 0 ? (
            <div className="text-center py-16 text-brand-muted">
              <Briefcase className="w-12 h-12 mx-auto mb-4 text-brand-primary/30" />
              <p>No active vacancies at the moment. Please check back later.</p>
            </div>
          ) : (
            <div className="space-y-10 lg:space-y-14" data-testid="vacancies-list">
              {list.map((v, i) => (
                <motion.div
                  key={v.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.05 }}
                  className="bg-white rounded-3xl overflow-hidden border border-brand-primary/10 shadow-[0_8px_30px_rgba(15,76,58,0.06)] grid lg:grid-cols-5 gap-0"
                  data-testid={`vacancy-${i}`}
                >
                  {/* Newspaper image side */}
                  <div className="lg:col-span-2 bg-brand-accent/40 p-5 lg:p-8 flex items-center justify-center">
                    <a
                      href={v.image_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block group relative rounded-2xl overflow-hidden border-2 border-brand-primary/15 hover:border-brand-secondary transition-colors"
                      data-testid={`vacancy-image-${i}`}
                      title="Open original newspaper advertisement"
                    >
                      <img
                        src={v.image_url}
                        alt={`${v.title} — newspaper ad`}
                        className="w-full h-auto block"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-brand-primary/0 group-hover:bg-brand-primary/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all">
                        <span className="bg-brand-secondary text-brand-primary px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2">
                          <FileText className="w-4 h-4" /> View Full Ad
                        </span>
                      </div>
                    </a>
                  </div>

                  {/* Details */}
                  <div className="lg:col-span-3 p-6 lg:p-10">
                    <div className="flex items-center gap-2 mb-3">
                      <Briefcase className="w-4 h-4 text-brand-secondary" />
                      <span className="text-xs uppercase tracking-[0.2em] text-brand-secondary font-semibold">
                        Vacancy
                      </span>
                    </div>
                    <h2 className="font-serif text-2xl lg:text-3xl text-brand-primary leading-tight mb-3">
                      {v.title}
                    </h2>
                    {v.posted_on && (
                      <div className="flex items-center gap-2 text-xs text-brand-muted mb-5">
                        <Calendar className="w-3.5 h-3.5" />
                        <span>{v.posted_on}</span>
                      </div>
                    )}
                    <p className="text-brand-ink/80 leading-relaxed text-sm lg:text-base mb-6">
                      {v.description}
                    </p>

                    {v.posts?.length > 0 && (
                      <>
                        <div className="text-xs uppercase tracking-[0.2em] text-brand-muted font-semibold mb-3">
                          Available posts
                        </div>
                        <ul className="space-y-2 mb-6">
                          {v.posts.map((p, j) => (
                            <li
                              key={j}
                              className="flex items-start gap-3 text-sm text-brand-ink/85"
                              data-testid={`vacancy-post-${i}-${j}`}
                            >
                              <span className="w-5 h-5 rounded-full bg-brand-accent text-brand-primary text-xs font-semibold flex items-center justify-center flex-shrink-0 mt-0.5">
                                {j + 1}
                              </span>
                              <span className="leading-relaxed">{p}</span>
                            </li>
                          ))}
                        </ul>
                      </>
                    )}

                    <div className="flex flex-wrap items-center gap-3 pt-5 border-t border-brand-primary/10">
                      <a
                        href={v.image_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-outline text-sm"
                        data-testid={`vacancy-download-${i}`}
                      >
                        <Download className="w-4 h-4" /> Original Ad
                      </a>
                      <a
                        href="/contact"
                        className="text-sm font-medium text-brand-primary hover:text-brand-secondary transition-colors"
                        data-testid={`vacancy-contact-${i}`}
                      >
                        Contact College Office →
                      </a>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          {/* Application instruction band */}
          <div className="mt-12 lg:mt-16 bg-brand-primary text-white rounded-3xl p-6 lg:p-10" data-testid="vacancy-howto">
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <Building2 className="w-6 h-6 text-brand-secondary mb-3" />
                <h3 className="font-serif text-xl mb-2 text-brand-secondary">
                  Where to apply
                </h3>
                <p className="text-white/80 text-sm leading-relaxed">
                  Application forms in triplicate are available at the college office between <strong>10 AM – 1 PM</strong>.
                </p>
              </div>
              <div>
                <FileText className="w-6 h-6 text-brand-secondary mb-3" />
                <h3 className="font-serif text-xl mb-2 text-brand-secondary">
                  Eligibility
                </h3>
                <p className="text-white/80 text-sm leading-relaxed">
                  As per <strong>UGC rules</strong> and the statutes of <strong>DDU Gorakhpur University</strong> and U.P. Government notifications.
                </p>
              </div>
              <div>
                <Calendar className="w-6 h-6 text-brand-secondary mb-3" />
                <h3 className="font-serif text-xl mb-2 text-brand-secondary">
                  Deadline
                </h3>
                <p className="text-white/80 text-sm leading-relaxed">
                  Applications must reach the college by registered post within <strong>25 days</strong> from the date of publication.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
